import { camelCase } from 'src/utils/camelCase'
import {
  IGetObjectsData,
  IObjectSorting,
  ISchema,
  ValueTypes,
} from 'src/services/storage/project/scheme/types'
import type {
  IColumn,
  IColumnRelation,
  TColumnKind,
} from 'src/store/modules/storage/objects/state'
import type { ISchemeRelations } from 'src/services/storage/project/scheme/relations/types'

import { IRelationLabelTexts, getRelationKindLabel } from './relationLabels'

/**
 * Pure helpers of the object table columns: the kind of a column, its
 * `sort[...]` key, its `related_fields[]` path and the stand-in object the
 * read-only related cells are rendered from. No store access, jest-testable.
 */

/** Identifier of the synthetic parent column */
export const PARENT_COLUMN_ID = 'parentObjectUniqId'

/** Legacy sort key of the parent column (primary field of the parent object) */
export const PARENT_PRIMARY_SORT_KEY = 'parent_object.primary'

/** Prefix of the `related_fields[]` / `sort[...]` paths of the parent scheme fields */
export const PARENT_PATH_PREFIX = 'parent_object.'

export const OWN_SECTION_KEY = 'own'
export const PARENT_SECTION_KEY = 'parent'

export const RELATED_TITLE_SEPARATOR = ' › '

const SORT_DIRECTIONS: string[] = ['asc', 'desc']

/**
 * Columns restored from an old localStorage cache have no `kind`: the main
 * column has no schema and the parent column has a fixed identifier.
 */
export const getColumnKind = (column: IColumn): TColumnKind => {
  if (column.kind) return column.kind
  if (!column.schema) return 'main'
  if (column.identifier === PARENT_COLUMN_ID) return 'parent'

  return 'own'
}

export const isRelatedColumnKind = (kind: TColumnKind) =>
  kind === 'parentField' || kind === 'relatedField'

export const isRelatedColumn = (column: IColumn) =>
  isRelatedColumnKind(getColumnKind(column))

/** `parent_object.<field>` or `<relationship field>.<field>` */
export const makeRelatedPath = (
  relation: Pick<IColumnRelation, 'kind' | 'fieldIdentifier'>,
  fieldIdentifier: string
) =>
  relation.kind === 'parent'
    ? `${PARENT_PATH_PREFIX}${fieldIdentifier}`
    : `${relation.fieldIdentifier}.${fieldIdentifier}`

/**
 * Relationship and media values of a related object are not returned by the
 * backend (they end up in `meta.skipped_related_fields`), hidden fields stay
 * hidden.
 */
export const isRelatedFieldCandidate = (field: ISchema) =>
  !field.isHidden &&
  field.valueType !== ValueTypes.RELATIONSHIP &&
  field.valueType !== ValueTypes.MEDIA

const isMediaSchema = (schema?: ISchema) =>
  schema?.valueType === ValueTypes.MEDIA

const isMultivalueRelationshipSchema = (schema?: ISchema) =>
  schema?.valueType === ValueTypes.RELATIONSHIP && Boolean(schema.isMultivalue)

/**
 * Key of `sort[<key>]` for the column, `null` when the backend cannot sort by
 * it: the main column, media values, multivalue relationship fields and the
 * fields behind a multivalue relationship (the cell shows a list).
 */
export const getColumnSortKey = (column: IColumn): string | null => {
  const kind = getColumnKind(column)

  if (kind === 'main') return null
  if (kind === 'parent') return PARENT_PRIMARY_SORT_KEY
  if (isMediaSchema(column.schema)) return null

  if (kind === 'own') {
    // A single-value relationship field is sorted by the primary field of the referenced object
    return isMultivalueRelationshipSchema(column.schema)
      ? null
      : column.identifier
  }

  if (kind === 'relatedField' && column.relation?.isMultivalue) return null

  return column.path || null
}

/** The main column sorts by the primary field of the scheme */
export const getPrimarySortKey = (
  primary?: Pick<ISchema, 'identifier'> | null
): string | null => primary?.identifier || null

export const getSortDirection = (
  sortKey: string | null | undefined,
  sorting?: IObjectSorting | null
): IObjectSorting[string] | undefined =>
  sortKey && sorting ? sorting[sortKey] : undefined

export const getActiveSortDirection = (
  column: IColumn,
  sorting?: IObjectSorting | null
) => getSortDirection(getColumnSortKey(column), sorting)

/** "Industry" for own fields, "Company › Industry" for related ones */
export const getColumnTitle = (column: IColumn, fallback?: string): string => {
  const title = column.schema?.title || fallback || column.identifier
  const { relation } = column

  if (!isRelatedColumn(column) || !relation) return title

  // Two relationship fields may point at the same scheme: the field title tells them apart
  const prefix =
    relation.kind === 'parent'
      ? relation.schemeTitle
      : relation.fieldTitle || relation.schemeTitle

  return `${prefix}${RELATED_TITLE_SEPARATOR}${title}`
}

export const getRelationSectionKey = (fieldIdentifier: string) =>
  `rel:${fieldIdentifier}`

/** Group of the column chooser: "This object", the parent scheme or one relationship field */
export const getColumnSectionKey = (column: IColumn): string => {
  const kind = getColumnKind(column)

  if (kind === 'parentField') return PARENT_SECTION_KEY
  if (kind === 'relatedField') {
    return getRelationSectionKey(column.relation?.fieldIdentifier || '')
  }

  return OWN_SECTION_KEY
}

/** `related_fields[]` of the list request: the paths of the enabled related columns */
export const getRelatedFieldsParam = (
  columns: IColumn[]
): string[] | undefined => {
  const paths = columns
    .filter((column) => column.enable && column.path && isRelatedColumn(column))
    .map((column) => column.path as string)
  const unique = Array.from(new Set(paths)).sort()

  return unique.length ? unique : undefined
}

export const collectSortKeys = (
  columns: IColumn[],
  primaryIdentifier?: string
): Set<string> => {
  const keys = new Set<string>()

  columns.forEach((column) => {
    const key = getColumnSortKey(column)

    if (key) keys.add(key)
  })

  if (primaryIdentifier) keys.add(primaryIdentifier)

  return keys
}

/**
 * Drops the sort keys the backend would reject with 422 (a related column
 * that is gone, a bad direction). Passes the sorting through while the
 * columns are not set up yet (only the main column or nothing).
 */
export const sanitizeSorting = (
  sorting: IObjectSorting | undefined,
  columns: IColumn[],
  primaryIdentifier?: string
): IObjectSorting | undefined => {
  if (!sorting) return undefined
  if (columns.length < 2) return sorting

  const keys = collectSortKeys(columns, primaryIdentifier)
  const entries = Object.entries(sorting).filter(
    ([key, direction]) =>
      keys.has(key) && SORT_DIRECTIONS.includes(String(direction))
  )

  if (!entries.length) return undefined
  if (entries.length === Object.keys(sorting).length) return sorting

  return Object.fromEntries(entries)
}

export interface IRelatedCell {
  object: IGetObjectsData
  schema: ISchema
}

const isPresent = (value: unknown) => value !== null && value !== undefined

/**
 * Stand-in object the `ObjectValue` renderers read a related value from:
 * the value sits under the key of the related field and `allowedActions` is
 * empty, which keeps the cell read-only (the inline edit form is never
 * mounted, it would PUT to the current scheme). For a multivalue
 * relationship the backend returns one entry per related object (`null` for
 * a related object that is gone): the entries are flattened one level and
 * the schema is shown as a list.
 */
export const getRelatedCellObject = (
  object: IGetObjectsData,
  column: IColumn
): IRelatedCell | null => {
  const { schema, relation, path } = column

  if (!schema || !relation || !path) return null

  // The keys of `related_values` are the raw paths: axios-case-converter preserves dotted keys
  const raw = object.relatedValues?.[path]
  let value: unknown = raw
  let cellSchema = schema

  if (relation.isMultivalue) {
    const entries = Array.isArray(raw) ? raw : []

    value = entries
      .filter(isPresent)
      .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
      .filter(isPresent)
    cellSchema = { ...schema, isMultivalue: true }
  }

  return {
    schema: cellSchema,
    object: {
      ...object,
      uniqId: `${object.uniqId}:${path}`,
      objectSchemeIdentifier: relation.schemeIdentifier,
      parentObject: undefined,
      // Same key as getObjectValueKey() of the helpers module (not imported to keep this module dependency-free)
      values: {
        [camelCase(schema.identifier)]: value,
      } as IGetObjectsData['values'],
      allowedActions: [],
    },
  }
}

export interface IColumnSection {
  key: string
  /** "This object" or the title of the related scheme */
  title: string
  /** "parent" / "field: Deals"; none for "This object" */
  kindLabel?: string
  columns: IColumn[]
}

export interface IColumnSectionLabels {
  thisObject: string
  relation: IRelationLabelTexts
}

/**
 * Groups of the column chooser: "This object" first, then the parent scheme
 * and one group per visible relationship field in the order of the relations.
 * Groups without columns (fields still loading, nothing displayable) are left out.
 */
export const buildColumnSections = (
  columns: IColumn[],
  relations: ISchemeRelations | null | undefined,
  labels: IColumnSectionLabels
): IColumnSection[] => {
  const columnsBySection = new Map<string, IColumn[]>()
  const sortedColumns = [...columns].sort(
    (a, b) => a.priority.index - b.priority.index
  )

  sortedColumns.forEach((column) => {
    const key = getColumnSectionKey(column)

    columnsBySection.set(key, [...(columnsBySection.get(key) || []), column])
  })

  const sections: IColumnSection[] = [
    {
      key: OWN_SECTION_KEY,
      title: labels.thisObject,
      columns: columnsBySection.get(OWN_SECTION_KEY) || [],
    },
  ]

  if (relations?.parent) {
    sections.push({
      key: PARENT_SECTION_KEY,
      title: relations.parent.title,
      kindLabel: getRelationKindLabel(labels.relation, 'parent'),
      columns: columnsBySection.get(PARENT_SECTION_KEY) || [],
    })
  }

  relations?.outgoing
    .filter((field) => !field.isHidden)
    .forEach((field) => {
      const key = getRelationSectionKey(field.identifier)

      sections.push({
        key,
        title: field.objectScheme.title,
        kindLabel: getRelationKindLabel(labels.relation, 'field', field.title),
        columns: columnsBySection.get(key) || [],
      })
    })

  return sections.filter((section) => section.columns.length > 0)
}
